import axios from 'axios'
import { getCookie } from './getCookies'
import { getHost } from './getHost'

export type PdfFile = {
  blob: Blob
  filename: string
}

export function defaultDocumentFilename(): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    '-',
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join('')
  return `document-${stamp}.pdf`
}

/** Скачивание без открытия PDF во вкладке/окне (Chrome иначе может показать viewer) */
export function downloadPdfBlob(blob: Blob, filename?: string) {
  const name = filename || defaultDocumentFilename()
  const file = new Blob([blob], { type: 'application/octet-stream' })
  const objectUrl = URL.createObjectURL(file)
  const link = document.createElement('a')
  link.href = objectUrl
  link.download = name
  link.rel = 'noopener'
  link.setAttribute('data-alex-pdf-download', '1')
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000)
}

/**
 * В dev PDF качаем через same-origin proxy (setupProxy + followRedirects).
 * Иначе signed URL (/pdfAdvance?signature) отвечает 302 с ?t=,
 * браузер на cross-origin редиректе снимает Authorization → HTML вместо PDF.
 */
function toRequestUrl(url: string): string {
  if (url.startsWith('blob:') || url.startsWith('data:')) return url

  const absolute = new URL(url, `${getHost().replace(/\/$/, '')}/`)
  const apiOrigin = new URL(getHost()).origin

  if (
    process.env.NODE_ENV === 'development' &&
    absolute.origin === apiOrigin &&
    absolute.origin !== window.location.origin
  ) {
    return `${absolute.pathname}${absolute.search}`
  }

  return absolute.href
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null

  const utf8 = /filename\*\s*=\s*(?:UTF-8''|utf-8'')([^;]+)/i.exec(header)
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^["']|["']$/g, ''))
    } catch {
      return utf8[1].trim().replace(/^["']|["']$/g, '')
    }
  }

  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header)
  if (plain?.[2]) {
    return plain[2].trim()
  }

  return null
}

function filenameFromUrl(url: string): string | null {
  try {
    const path = new URL(url, window.location.origin).pathname
    const last = path.split('/').filter(Boolean).pop()
    if (last && /\.pdf$/i.test(last)) return decodeURIComponent(last)
  } catch {
    // ignore
  }
  return null
}

function isPdfMagic(bytes: Uint8Array): boolean {
  let offset = 0
  if (
    bytes.length >= 3 &&
    bytes[0] === 0xef &&
    bytes[1] === 0xbb &&
    bytes[2] === 0xbf
  ) {
    offset = 3
  }
  if (bytes.length < offset + 5) return false
  return (
    String.fromCharCode(
      bytes[offset],
      bytes[offset + 1],
      bytes[offset + 2],
      bytes[offset + 3],
      bytes[offset + 4]
    ) === '%PDF-'
  )
}

async function asPdfBlob(blob: Blob): Promise<Blob> {
  const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer())
  if (!isPdfMagic(head)) {
    throw new Error('Ответ не является PDF')
  }
  return blob.type === 'application/pdf'
    ? blob
    : new Blob([blob], { type: 'application/pdf' })
}

function resolveFilename(
  header: string | null,
  requestUrl: string,
  fallbackTitle?: string
): string {
  return (
    filenameFromDisposition(header) ||
    filenameFromUrl(requestUrl) ||
    (fallbackTitle
      ? `${fallbackTitle.replace(/[\\/:*?"<>|]+/g, '_').trim()}.pdf`
      : '') ||
    defaultDocumentFilename()
  )
}

async function tryFetch(
  requestUrl: string,
  headers: Record<string, string>,
  fallbackTitle?: string
): Promise<PdfFile | null> {
  try {
    const response = await fetch(requestUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
    if (!response.ok) return null
    const blob = await asPdfBlob(await response.blob())
    const filename = resolveFilename(
      response.headers.get('content-disposition'),
      requestUrl,
      fallbackTitle
    )
    return { blob, filename }
  } catch {
    return null
  }
}

async function fetchPdfBlobUncached(
  url: string,
  fallbackTitle?: string
): Promise<PdfFile> {
  const requestUrl = toRequestUrl(url)

  if (requestUrl.startsWith('blob:') || requestUrl.startsWith('data:')) {
    const response = await fetch(requestUrl)
    if (!response.ok) throw new Error('Не удалось загрузить PDF')
    const blob = await asPdfBlob(await response.blob())
    return {
      blob,
      filename: resolveFilename(null, requestUrl, fallbackTitle),
    }
  }

  const token = getCookie('auth')
  const accept = {
    Accept: 'application/pdf,application/octet-stream,*/*',
  }

  const withAuth = await tryFetch(
    requestUrl,
    {
      ...accept,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    fallbackTitle
  )
  if (withAuth) return withAuth

  const withCookies = await tryFetch(requestUrl, accept, fallbackTitle)
  if (withCookies) return withCookies

  const response = await axios.get(requestUrl, {
    responseType: 'blob',
    withCredentials: true,
    headers: {
      ...accept,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  const blob = await asPdfBlob(response.data)
  const disposition =
    (response.headers['content-disposition'] as string | undefined) ||
    (response.headers['Content-Disposition'] as string | undefined) ||
    null

  return {
    blob,
    filename: resolveFilename(disposition, requestUrl, fallbackTitle),
  }
}

export async function fetchPdfBlob(
  url: string,
  fallbackTitle?: string
): Promise<PdfFile> {
  return fetchPdfBlobUncached(url, fallbackTitle)
}
